import { Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  OverviewCard,
  valueWithText,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import UtilizationCard from '@HomaHealth/ui-component-library/lib/_AIQ-components/molecules/UtilizationCard';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import {
  TotalCost,
  PMPM,
  PEPM,
  TotalEnrolled,
  formatTotalCost,
  formatPMPM,
  formatPEPM,
  formatTotalEnrolled,
  getPmpmForCohorts,
  getPmpmValueForCohorts,
  getPepmForCohorts,
  getPepmValueForCohorts,
} from '../../core-utils/helper-utils/summaryReportHelper';
import componentConstants from '../../core-utils/messages/componentConstants';
import properties from '../../core-utils/properties';
import Loader from '../../_components/molecules/Loader';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import Snackbar from '../../_components/atoms/Snackbar';
import {
  numFormatterWithSuffix,
  roundNumber,
} from '../../core-utils/helper-utils/helper';
import { formatNetworkUtilizationCardData } from '../../core-utils/formatters/UtilizationSummaryPageFormatters';
import { useStores } from '../../stores';
import messages from '../../core-utils/messages';
import { COMMON_TEXTS } from '../../core-utils/messages/CommonConstants';
import ReportsModal from '../../_components/organisms/ReportsModal';
import { useNavigate } from 'react-router-dom';
import {
  PATIENT_ADMISSION,
  PROCEDURE_UTILIZATION,
  PROVIDER_UTILIZATION,
  RX_UTILIZATION,
} from '../../core-utils/routes';
import TabSelection from '../../_components/molecules/TabSelection';
import RecommendationsTab from '../../_components/organisms/RecommendationsTab';
import { getRecommendationsDataForUtilizationMetrics } from '../../core-utils/helper-utils/recommendationsTabHelper';
import EditDurationModal from '../../_components/molecules/EditDurationModal';
import PageTitleAndSubTitle from '@HomaHealth/ui-component-library/lib/_AIQ-components/molecules/PageTitleAndSubtitle';
import {
  UserAccessConstants,
  checkUserAccess,
} from '../../core-utils/helper-utils/userAccess';
import CohortDetailsModal from '../../_components/organisms/CohortDetailsModal';
import FeatureAccessModal from '../../_components/organisms/FeatureAccessModal';
import { utilizationmessages } from '../../core-utils/messages/utilizationProcedureConstants';
import { observer } from 'mobx-react';
import QueryIcon from '../../_components/atoms/Icons/QueryIcon';

export interface IRxUtilizationCardData {
  totalRxCost: number;
  rxPMPM: number;
  rxCostChangePercent: number;
  rxPMPMChangePercent: number;
  brandedCost: number;
  genericCost: number;
}
export interface IMedicalUtilizationCardData {
  total_cost: number;
  in_patient_cost: number;
  other_cost: number;
  pmpm: number;
  total_cost_change_percentage: number;
  pmpm_change_percentage: number;
}
export interface IProcedureUtilizationCardData {
  totalCost: number;
  procedurePMPM: number;
  costChangePercent: number;
  pmpmChangePercent: number;
  surgicalCost: number;
  nonSurgicalCost: number;
}

export interface INetworkUtilizationCardData {
  providerInNetwork: number;
  providerOutNetwork: number;
  facilityInNetwork: number;
  facilityOutNetwork: number;
}

export interface UtilizationSummaryPageProps {}

const UtilizationSummaryPage: React.FC<UtilizationSummaryPageProps> = observer(
  ({}) => {
    // styles
    const useStyles = makeStyles()({
      root: {
        display: 'flex',
        flexDirection: 'column',
        margin: '40px 64px 28px 64px',
      },
      headerContainer: {
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: 'fit-content',
        marginBottom: '20px',
      },
      tabsRoot: {
        minHeight: '33px',
        height: '33px',
      },
      tab: {
        textTransform: 'none',
        minHeight: '33px',
        height: '33px',
        minWidth: 'fit-content',
        width: 'fit-content',
        padding: '0px',
      },
      selectedTab: {
        color: `${patientAccessTheme.palette.text.highemphasis} !important`,
      },
      recommendedTab: {
        width: '1500px',
        height: '493px',
        padding: '20px',
      },
      queryDataOverallGrid: {
        margin: '10px 10px 10px 0px',
        height: '44px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #DADEEF',
        width: '1176px',
        borderRadius: '4px',
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      queryIconGrid: {
        padding: '10px 0px 10px 22px',
      },
      queryDataTypography: {
        padding: '10px 10px',
        height: '44px',
        borderRight: '1px solid #DADEEF',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      queryDataGrid: {
        maxWidth: '400px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: '60px',
      },
      editQueryGrid: {
        padding: '10px 40px',
        backgroundColor: '#FFFFFF',
        border: '1px solid #DADEEF',
      },
      tooltip: {
        backgroundColor: patientAccessTheme.palette.grey[900],
        padding: '10px',
        fontSize: '13px',
        fontFamily: 'Inter',
        fontWeight: 400,
        lineHeight: '16px',
        color: patientAccessTheme.palette.status.white,
      },
      arrow: {
        color: patientAccessTheme.palette.grey[900],
        marginLeft: '50px',
      },
    });

    const { classes } = useStyles();

    // constants
    const pageConstants = componentConstants.PAGES.UTILIZATION_SUMMARY_PAGE;

    // states
    const [loading, setLoading] = useState<boolean>(false);
    const [totalEnrolled, setTotalEnrolled] = useState<TotalEnrolled>({
      dependents: '',
      totalCount: '',
      employees: '',
    });
    const [totalCost, setTotalCost] = useState<TotalCost>({
      totalCost: '',
      totalMedical: '',
      totalRx: '',
    });
    const [pmpm, setPMPM] = useState<PMPM>({
      pmpm: '',
      pmpmPercentage: '',
      increment: true,
    });
    const [pepm, setPEPM] = useState<PEPM>({
      pepm: '',
      pepmPercentage: '',
      increment: true,
    });
    const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
    const [
      rxUtilizationCardData,
      setRxUtilizationCardData,
    ] = useState<IRxUtilizationCardData>({
      totalRxCost: 0,
      rxPMPM: 0,
      rxCostChangePercent: 0,
      rxPMPMChangePercent: 0,
      brandedCost: 0,
      genericCost: 0,
    });
    const [
      medicalUtilizationCardData,
      setMedicalUtilizationCardData,
    ] = useState<IMedicalUtilizationCardData>({
      total_cost: 0,
      in_patient_cost: 0,
      other_cost: 0,
      pmpm: 0,
      total_cost_change_percentage: -0,
      pmpm_change_percentage: 0,
    });
    const [
      networkUtilizationCardData,
      setNetworkUtilizationCardData,
    ] = useState<INetworkUtilizationCardData>({
      providerInNetwork: 0,
      providerOutNetwork: 0,
      facilityInNetwork: 0,
      facilityOutNetwork: 0,
    });
    const [
      procedureUtilizationCardData,
      setProcedureUtilizationCardData,
    ] = useState<IProcedureUtilizationCardData>({
      totalCost: 0,
      procedurePMPM: 0,
      costChangePercent: 0,
      pmpmChangePercent: 0,
      surgicalCost: 0,
      nonSurgicalCost: 0,
    });
    const [openReportsModal, setOpenReportsModal] = useState(false);
    const [openCohortDetailsModal, setOpenCohortDetailsModal] = useState(false);
    const [openFeatureAccessModal, setOpenFeatureAccessModal] = useState(false);

    //snackbar
    const handleOpenSnackBar = (
      message: string,
      severity: MuiColor,
      duration: number | null,
    ) => {
      setSnackbarState({
        open: true,
        message: message,
        severity: severity,
        duration: duration,
      });
    };

    const handleCloseSnackBar = (
      event?: React.SyntheticEvent,
      reason?: string,
    ) => {
      if (reason === 'clickaway') {
        return;
      }
      setSnackbarState({
        open: false,
        message: '',
        severity: undefined,
        duration: null,
      });
    };

    const [snackbarState, setSnackbarState] = React.useState<{
      open: boolean;
      message: string;
      severity: MuiColor | undefined;
      duration: null | number;
    }>({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });

    const [recommendationLoading, setRecommendationLoading] = useState<boolean>(
      false,
    );
    const [recommendationFetched, setRecommendationFetched] = useState<boolean>(
      false,
    );
    const handleSelectedIndex = (index: number) => {
      setSelectedTabIndex(index);
      if (!recommendationFetched) {
        getRecommendationsDataForUtilizationMetrics(
          employerGroupStore?.employerGroupResponseData?.eg_nid,
          [
            pageConstants.RX_CARD.TITLE,
            pageConstants.PROCEDURE_CARD.TITLE,
            pageConstants.PROVIDER_UTILIZATION,
            pageConstants.ADMISSION_UTLIZATION,
          ],
          recommendedTabStore,
          setRecommendationLoading,
        );
        setRecommendationFetched(true);
      }
    };
    const handleCohortDetailsClose = () => {
      setOpenCohortDetailsModal(false);
      cohortDetailsStore.setCohortDetailsList([
        {
          id: 1,
          category: '',
          condition: '',
          property: '',
          results: '',
          dropdownSelectionTypeIndex: 0,
          andOrCondition: 'AND',
          propertiesPageNumber: 0,
          totalPagesForProperties: 0,
        },
      ]);
      cohortDetailsStore.removeConditionsDetails();
      cohortDetailsStore.removeClaimIdList();
      cohortDetailsStore.removeMemberUniqueIdList();
    };

    //api calls
    const getTotalEligible = async () => {
      await axios
        .post(properties.aiqBaseUrl + properties.summaryBaseUrl, {
          startDate: startDate,
          endDate: endDate,
          eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
          claim_id_list: cohortDetailsStore.claimIdList,
          member_unique_id_list: cohortDetailsStore.memberUniqueIdList,
          max_claim_date:
            employerGroupStore?.employerGroupResponseData?.max_claim_date,
        })
        .then((res) => {
          if (res?.status === 200) {
            setTotalEnrolled(formatTotalEnrolled(res?.data?.data));
            setTotalCost(formatTotalCost(res?.data?.data));
          }
        })
        .catch((error) => {
          return error;
        });
    };

    const getPMPMAndPepm = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.summaryBaseUrl +
            properties.pmpmAndPepm,
          {
            startDate: startDate,
            endDate: endDate,
            eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
            claim_id_list: cohortDetailsStore.claimIdList,
            member_unique_id_list: cohortDetailsStore.memberUniqueIdList,
            max_claim_date:
              employerGroupStore?.employerGroupResponseData?.max_claim_date,
          },
        )
        .then((res) => {
          if (res?.status === 200) {
            setPMPM(formatPMPM(res?.data?.data?.pmpm));
            setPEPM(formatPEPM(res?.data?.data?.pepm));
          }
        })
        .catch((error) => {
          return error;
        });
    };

    const getRxUtilizationCardData = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.utilizationRxBaseUrl +
            properties.rxUtilization,
          {
            startDate,
            endDate,
            eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
            claim_id_list: cohortDetailsStore.claimIdList,
            member_unique_id_list: cohortDetailsStore.memberUniqueIdList,
          },
        )
        .then((response) => {
          setRxUtilizationCardData(response?.data?.data);
        })
        .catch((error) => {
          handleOpenSnackBar(
            error.response?.data?.error ?? messages.SOMETHING_WENT_WRONG,
            'error',
            8000,
          );
        });
    };

    const getProcedureUtilizationCardData = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.utilizationRxBaseUrl +
            properties.procedureUtilization,
          {
            startDate,
            endDate,
            eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
            claim_id_list: cohortDetailsStore.claimIdList,
            member_unique_id_list: cohortDetailsStore.memberUniqueIdList,
          },
        )
        .then((response) => {
          setProcedureUtilizationCardData(response?.data?.data);
        })
        .catch((error) => {
          handleOpenSnackBar(
            error.response?.data?.error ?? messages.SOMETHING_WENT_WRONG,
            'error',
            8000,
          );
        });
    };

    const getMedicalServicesUtilizationCardData = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.utilizationRxBaseUrl +
            properties.medicalUtilization,
          {
            startDate,
            endDate,
            eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
            claim_id_list: cohortDetailsStore.claimIdList,
            member_unique_id_list: cohortDetailsStore.memberUniqueIdList,
          },
        )
        .then((response) => {
          setMedicalUtilizationCardData(response?.data?.data);
        })
        .catch((error) => {
          handleOpenSnackBar(
            error.response?.data?.error ?? messages.SOMETHING_WENT_WRONG,
            'error',
            8000,
          );
        });
    };

    const getNetworkProviderUtilizationCardData = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.utilizationRxBaseUrl +
            properties.utilizationNetwork,
          {
            startDate,
            endDate,
            eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
            claim_id_list: cohortDetailsStore.claimIdList,
            member_unique_id_list: cohortDetailsStore.memberUniqueIdList,
          },
        )
        .then((response) => {
          setNetworkUtilizationCardData(
            formatNetworkUtilizationCardData(response?.data?.data),
          );
        })
        .catch((error) => {
          handleOpenSnackBar(
            error.response?.data?.error ?? messages.SOMETHING_WENT_WRONG,
            'error',
            8000,
          );
        });
    };

    const recommendedTabStore = useStores().recommendedTabStore;

    const getPageData = async () => {
      setLoading(true);
      Boolean(startDate !== '' && endDate !== '')
        ? await Promise.all([
            getTotalEligible(),
            getPMPMAndPepm(),
            getRxUtilizationCardData(),
            getMedicalServicesUtilizationCardData(),
            getNetworkProviderUtilizationCardData(),
            getProcedureUtilizationCardData(),
          ]).then(() => {
            setLoading(false);
          })
        : '';
      setLoading(false);
    };

    // Edit duration related states and functions

    const employerGroupStore = useStores().employerGroupStore;
    const cohortDetailsStore = useStores().cohortDetailsStore;
    const loginPageStore = useStores().loginPageStore;
    const summaryCardsStore = useStores().summaryCardsStore;
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [areDatesReady, setAreDatesReady] = useState<boolean>(false);

    const handleDateChange = () => {
      setStartDate(employerGroupStore?.selectedDuration?.startDate);
      setEndDate(employerGroupStore?.selectedDuration?.endDate);
      setAreDatesReady((prev) => !prev);
    };
    const navigate = useNavigate();

    // useEffects
    useEffect(() => {
      Boolean(startDate !== '' && endDate !== '') ? getPageData() : '';
    }, [areDatesReady, cohortDetailsStore.conditionsDetails]);

    useEffect(() => {
      setStartDate(employerGroupStore?.selectedDuration?.startDate);
      setEndDate(employerGroupStore?.selectedDuration?.endDate);
      setAreDatesReady((prev) => !prev);
    }, []);

    //Click-here Navigation Functions:
    const pharmacyUtilization = () => {
      navigate(RX_UTILIZATION);
    };
    const procedureUtilization = () => {
      navigate(PROCEDURE_UTILIZATION);
    };
    const medicalService = () => {
      navigate(PATIENT_ADMISSION);
    };
    const providerUtil = () => {
      navigate(PROVIDER_UTILIZATION);
    };

    //tab content
    const getTabContent = (index: number) => {
      switch (index) {
        case 0:
          return (
            <Grid container>
              {cohortDetailsStore.conditionsDetails &&
                cohortDetailsStore.conditionsDetails?.length > 0 && (
                  <Grid
                    container
                    direction="row"
                    className={classes.queryDataOverallGrid}
                  >
                    <Grid className={classes.queryIconGrid}>
                      <QueryIcon />
                    </Grid>
                    {cohortDetailsStore.conditionsDetails?.map(
                      (detail, index: number) => {
                        return (
                          <Tooltip
                            key={index}
                            arrow={true}
                            placement="bottom-end"
                            classes={{
                              tooltip: classes.tooltip,
                              arrow: classes.arrow,
                            }}
                            title={
                              <Grid
                                container
                                sx={{
                                  flexDirection: 'column',
                                }}
                              >
                                {cohortDetailsStore.conditionsDetails?.map(
                                  (detail, index: number) => {
                                    return <Grid key={index}>{detail}</Grid>;
                                  },
                                )}
                              </Grid>
                            }
                          >
                            <Grid className={classes.queryDataGrid}>
                              <Typography
                                className={classes.queryDataTypography}
                                lineHeight="25px"
                                fontSize="14px"
                                fontType="regular"
                              >
                                {detail}
                              </Typography>
                            </Grid>
                          </Tooltip>
                        );
                      },
                    )}
                  </Grid>
                )}
              <Grid container style={{ margin: '36px 0px 28px 0px' }}>
                <Grid style={{ marginRight: '24px' }}>
                  <OverviewCard
                    title={pageConstants.OVERVIEW_CARDS.MEMBERS}
                    value={valueWithText(
                      totalEnrolled.totalCount.toString(),
                      '',
                    )}
                    rootStyles={{
                      width: '176px',
                    }}
                  />
                </Grid>

                <Grid style={{ marginRight: '24px' }}>
                  <OverviewCard
                    title={pageConstants.OVERVIEW_CARDS.TOTAL_COST}
                    value={totalCost?.totalCost}
                    isMultiValued={true}
                    rightLabelValues={[
                      ['Medical', totalCost?.totalMedical],
                      ['Rx', totalCost?.totalRx],
                    ]}
                    rootStyles={{
                      width: '276px',
                    }}
                  />
                </Grid>

                <Grid style={{ marginRight: '24px' }}>
                  <OverviewCard
                    title={pageConstants.OVERVIEW_CARDS.PMPM}
                    topLabelValues={getPmpmForCohorts(pmpm, [], [])}
                    value={getPmpmValueForCohorts(pmpm, [], [])}
                    rootStyles={{
                      width: '176px',
                    }}
                  />
                </Grid>

                <Grid>
                  <OverviewCard
                    title={pageConstants.OVERVIEW_CARDS.PEPM}
                    topLabelValues={getPepmForCohorts(pepm, [], [])}
                    value={getPepmValueForCohorts(pepm, [], [])}
                    rootStyles={{
                      width: '176px',
                    }}
                  />
                </Grid>
              </Grid>
              <Grid style={{ marginBottom: '16px' }}>
                <Typography
                  fontSize="18px"
                  fontType="medium"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                  lineHeight="26px"
                >
                  {pageConstants.UTILIZATION}
                </Typography>
              </Grid>
              <Grid container>
                <Grid style={{ margin: '0px 24px 28px 0px' }}>
                  <UtilizationCard
                    title={pageConstants.RX_CARD.TITLE}
                    subTitle={pageConstants.RX_CARD.SUBTITLE}
                    clickHere={pageConstants.CLICK_HERE}
                    labels={pageConstants.RX_CARD.LABELS}
                    values={[
                      {
                        value:
                          '$' +
                          numFormatterWithSuffix(
                            rxUtilizationCardData?.totalRxCost,
                          ),
                        percentage: roundNumber(
                          rxUtilizationCardData?.rxCostChangePercent,
                          1,
                        ),
                        increased:
                          rxUtilizationCardData?.rxCostChangePercent >= 0,
                      },
                      {
                        value:
                          '$' +
                          numFormatterWithSuffix(rxUtilizationCardData?.rxPMPM),
                        percentage: roundNumber(
                          rxUtilizationCardData?.rxPMPMChangePercent,
                          1,
                        ),
                        increased:
                          rxUtilizationCardData?.rxPMPMChangePercent >= 0,
                      },
                    ]}
                    ratioData={[
                      {
                        labels: pageConstants.RX_CARD.RATIO_DATA_LABELS,
                        values: [
                          rxUtilizationCardData?.brandedCost,
                          rxUtilizationCardData?.genericCost,
                        ],
                      },
                    ]}
                    formatNumber={true}
                    clickHereFunction={pharmacyUtilization}
                  />
                </Grid>
                <Grid style={{ margin: '0px 24px 28px 0px' }}>
                  <UtilizationCard
                    title={pageConstants.PROCEDURE_CARD.TITLE}
                    subTitle={pageConstants.PROCEDURE_CARD.SUBTITLE}
                    clickHere={pageConstants.CLICK_HERE}
                    labels={pageConstants.PROCEDURE_CARD.LABELS}
                    values={[
                      {
                        value:
                          '$' +
                          numFormatterWithSuffix(
                            procedureUtilizationCardData?.totalCost,
                          ),
                        percentage: roundNumber(
                          procedureUtilizationCardData?.costChangePercent,
                          1,
                        ),
                        increased:
                          procedureUtilizationCardData?.costChangePercent >= 0,
                      },
                      {
                        value:
                          '$' +
                          numFormatterWithSuffix(
                            procedureUtilizationCardData?.procedurePMPM,
                          ),
                        percentage: roundNumber(
                          procedureUtilizationCardData?.pmpmChangePercent,
                          1,
                        ),
                        increased:
                          procedureUtilizationCardData?.pmpmChangePercent >= 0,
                      },
                    ]}
                    ratioData={[
                      {
                        labels: pageConstants.PROCEDURE_CARD.RATIO_DATA_LABELS,
                        values: [
                          procedureUtilizationCardData.surgicalCost,
                          procedureUtilizationCardData.nonSurgicalCost,
                        ],
                      },
                    ]}
                    formatNumber={true}
                    clickHereFunction={procedureUtilization}
                  />
                </Grid>
                <Grid style={{ marginBottom: '28px' }}>
                  <UtilizationCard
                    title={pageConstants.MEDICAL_SERVICES_CARD.TITLE}
                    subTitle={pageConstants.MEDICAL_SERVICES_CARD.SUBTITLE}
                    clickHere={pageConstants.CLICK_HERE}
                    labels={pageConstants.MEDICAL_SERVICES_CARD.LABELS}
                    values={[
                      {
                        value:
                          '$' +
                          numFormatterWithSuffix(
                            medicalUtilizationCardData?.total_cost,
                          ),
                        percentage: roundNumber(
                          medicalUtilizationCardData?.total_cost_change_percentage,
                          1,
                        ),
                        increased:
                          medicalUtilizationCardData?.total_cost_change_percentage >=
                          0,
                      },
                      {
                        value:
                          '$' +
                          numFormatterWithSuffix(
                            medicalUtilizationCardData?.pmpm,
                          ),
                        percentage: roundNumber(
                          medicalUtilizationCardData.pmpm_change_percentage,
                          1,
                        ),
                        increased:
                          medicalUtilizationCardData.pmpm_change_percentage >=
                          0,
                      },
                    ]}
                    ratioData={[
                      {
                        labels:
                          pageConstants.MEDICAL_SERVICES_CARD.RATIO_DATA_LABELS,
                        values: [
                          medicalUtilizationCardData?.in_patient_cost,
                          medicalUtilizationCardData?.other_cost,
                        ],
                      },
                    ]}
                    formatNumber={true}
                    clickHereFunction={medicalService}
                  />
                </Grid>
                <Grid>
                  <UtilizationCard
                    title={pageConstants.NETWORK_PROVIDER_CARD.TITLE}
                    subTitle={pageConstants.NETWORK_PROVIDER_CARD.SUBTITLE}
                    clickHere={pageConstants.CLICK_HERE}
                    labels={[]}
                    values={[]}
                    ratioData={[
                      {
                        labels:
                          pageConstants.NETWORK_PROVIDER_CARD.RATIO_DATA_LABELS
                            .LABELS1,
                        values: [
                          networkUtilizationCardData.providerInNetwork,
                          networkUtilizationCardData.providerOutNetwork,
                        ],
                      },
                      {
                        labels:
                          pageConstants.NETWORK_PROVIDER_CARD.RATIO_DATA_LABELS
                            .LABELS2,
                        values: [
                          networkUtilizationCardData.facilityInNetwork,
                          networkUtilizationCardData.facilityOutNetwork,
                        ],
                      },
                    ]}
                    formatNumber={true}
                    clickHereFunction={providerUtil}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        case 1:
          return (
            <div className={classes.recommendedTab}>
              <RecommendationsTab
                recommendationLoading={recommendationLoading}
              />
            </div>
          );
      }
      return <></>;
    };

    const [loader] = useState<any>(<Loader />);

    return (
      <>
        {loading ? (
          loader
        ) : (
          <Grid
            container
            direction="row"
            style={{
              marginLeft: '100px',
              marginTop: '38px',
              width: '1245px',
              paddingBottom: '60px',
            }}
          >
            {/* Header */}
            <Grid
              style={{
                marginTop: '20px',
                position: 'relative',
              }}
            >
              <div style={{ width: '1182px' }}>
                <PageTitleAndSubTitle
                  title={pageConstants.TITLE}
                  subTitle={pageConstants.SUBTITLE}
                  showDownloadButton
                  downloadButtonAction={() => {}}
                  // showQueryDataButton
                  queryButtonText={
                    cohortDetailsStore.cohortDetailsList?.length > 0 &&
                    cohortDetailsStore.cohortDetailsList[0].category !== ''
                      ? 'Edit Cohort'
                      : 'Add Cohort'
                  }
                  queryDataAction={() => {
                    if (
                      properties.environment === 'dev' &&
                      checkUserAccess(
                        UserAccessConstants.COHORT_DETAILS,
                        loginPageStore.userResponse.role.name,
                        loginPageStore.userFeatures,
                      )
                    ) {
                      setOpenCohortDetailsModal(true);
                    } else {
                      setOpenFeatureAccessModal(true);
                    }
                  }}
                />
              </div>

              <div
                style={{
                  position: 'absolute',
                  top: '-2px',
                  left: '183px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  fontSize="17px"
                  lineHeight="21px"
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                  fontType="semiBold"
                  margin="0px 8px"
                >
                  {COMMON_TEXTS.FOR}
                </Typography>
                <EditDurationModal
                  isYearGrid={false}
                  handleDateChange={handleDateChange}
                />
              </div>
              <TabSelection
                selectedTabIndex={selectedTabIndex}
                setSelectedTabIndex={handleSelectedIndex}
                recommendationFetched={recommendationFetched}
                margin="0px"
              />
            </Grid>

            {getTabContent(selectedTabIndex)}
          </Grid>
        )}
        <Snackbar
          {...snackbarState}
          handleClose={handleCloseSnackBar}
          vertical="bottom"
          horizontal="right"
        />
        <ReportsModal
          open={openReportsModal}
          setOpen={setOpenReportsModal}
          startDate={startDate}
          endDate={endDate}
        />
        <CohortDetailsModal
          open={openCohortDetailsModal}
          isPage={true}
          setOpenCohortDetailsModal={setOpenCohortDetailsModal}
          handleClose={handleCohortDetailsClose}
          title={utilizationmessages.COHORT_DETAILS.TITLE}
          subTitle={utilizationmessages.COHORT_DETAILS.SUBTITLE}
          totalResults={summaryCardsStore.totalEnrolled?.totalCount}
          startDate={startDate}
          endDate={endDate}
          employerGroup={employerGroupStore?.employerGroupResponseData?.eg_nid}
          type="data"
        />
        <FeatureAccessModal
          open={openFeatureAccessModal}
          handleClose={() => {
            setOpenFeatureAccessModal(false);
          }}
          access={checkUserAccess(
            UserAccessConstants.COHORT_DETAILS,
            loginPageStore.userResponse.role.name,
            loginPageStore.userFeatures,
          )}
        ></FeatureAccessModal>
      </>
    );
  },
);

export default UtilizationSummaryPage;
